import React, { useEffect, useState } from "react";
import {
	createAdminUser,
	deleteAdminUser,
	getAllAdminUsers,
	resetAdminUserPassword,
	updateAdminUser,
} from "../../api/apiServiceFetch";
import ConfirmPopup from "../../components/ConfirmPopup";
import "./AdminUsers.scss";

const formatDateTime = (value) => {
	if (!value) return "—";

	if (typeof value === "string" && value.startsWith("0001-01-01")) {
		return "—";
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";

	return date.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const emptyNewAdminUser = {
	fullName: "",
	login: "",
	password: "",
	retypePassword: "",
	isActive: true,
	isSuperAdmin: false,
};

const emptyPasswordForm = {
	id: null,
	newPassword: "",
	retypeNewPassword: "",
};

const AdminUsers = () => {
	const [adminUsers, setAdminUsers] = useState([]);
	const [expandedUserId, setExpandedUserId] = useState(null);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [newAdminUser, setNewAdminUser] = useState(emptyNewAdminUser);

	const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
	const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);

	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);

	const [loading, setLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const fetchAdminUsers = async () => {
		try {
			setLoading(true);
			setErrorMessage("");

			const users = await getAllAdminUsers();
			setAdminUsers(Array.isArray(users) ? users : []);
		} catch (error) {
			console.error("Failed to load admin users:", error);
			setErrorMessage(error?.message || "Не удалось загрузить администраторов");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchAdminUsers();
	}, []);

	const handleExpandClick = (userId) => {
		setExpandedUserId((prev) => (prev === userId ? null : userId));
	};

	const handleCreateModalOpen = () => {
		setErrorMessage("");
		setNewAdminUser(emptyNewAdminUser);
		setIsModalOpen(true);
	};

	const handleCreateModalClose = () => {
		if (actionLoading) return;

		setIsModalOpen(false);
		setNewAdminUser(emptyNewAdminUser);
	};

	const handlePasswordModalOpen = (userId) => {
		setErrorMessage("");
		setPasswordForm({
			id: userId,
			newPassword: "",
			retypeNewPassword: "",
		});
		setIsPasswordModalOpen(true);
	};

	const handlePasswordModalClose = () => {
		if (actionLoading) return;

		setIsPasswordModalOpen(false);
		setPasswordForm(emptyPasswordForm);
	};

	const validateCreateForm = () => {
		if (!newAdminUser.fullName.trim()) {
			setErrorMessage("Укажите имя и фамилию");
			return false;
		}

		if (!newAdminUser.login.trim()) {
			setErrorMessage("Укажите логин");
			return false;
		}

		if (!newAdminUser.password.trim()) {
			setErrorMessage("Укажите пароль");
			return false;
		}

		if (newAdminUser.password !== newAdminUser.retypePassword) {
			setErrorMessage("Пароли не совпадают");
			return false;
		}

		return true;
	};

	const validatePasswordForm = () => {
		if (!passwordForm.id) {
			setErrorMessage("Администратор не выбран");
			return false;
		}

		if (!passwordForm.newPassword.trim()) {
			setErrorMessage("Укажите новый пароль");
			return false;
		}

		if (passwordForm.newPassword !== passwordForm.retypeNewPassword) {
			setErrorMessage("Пароли не совпадают");
			return false;
		}

		return true;
	};

	const handleInsert = async () => {
		if (!validateCreateForm()) return;

		try {
			setActionLoading(true);
			setErrorMessage("");

			await createAdminUser({
				fullName: newAdminUser.fullName.trim(),
				login: newAdminUser.login.trim(),
				password: newAdminUser.password,
				retypePassword: newAdminUser.retypePassword,
				isActive: newAdminUser.isActive,
				isSuperAdmin: newAdminUser.isSuperAdmin,
			});

			await fetchAdminUsers();

			setNewAdminUser(emptyNewAdminUser);
			setIsModalOpen(false);
		} catch (error) {
			console.error("Create admin error:", error);
			setErrorMessage(error?.message || "Не удалось создать администратора");
		} finally {
			setActionLoading(false);
		}
	};

	const handleActivateDeactivate = async (user) => {
		if (!user?.id) return;

		const updatedUser = {
			id: user.id,
			fullName: user.fullName || "",
			login: user.login || "",
			isActive: !user.isActive,
			isSuperAdmin: Boolean(user.isSuperAdmin),
		};

		try {
			setActionLoading(true);
			setErrorMessage("");

			await updateAdminUser(updatedUser);

			setAdminUsers((prev) =>
				prev.map((item) =>
					item.id === user.id
						? { ...item, isActive: updatedUser.isActive }
						: item,
				),
			);
		} catch (error) {
			console.error("Update admin status error:", error);
			setErrorMessage(error?.message || "Не удалось обновить статус");
		} finally {
			setActionLoading(false);
		}
	};

	const handleToggleSuperAdmin = async (user) => {
		if (!user?.id) return;

		const updatedUser = {
			id: user.id,
			fullName: user.fullName || "",
			login: user.login || "",
			isActive: Boolean(user.isActive),
			isSuperAdmin: !user.isSuperAdmin,
		};

		try {
			setActionLoading(true);
			setErrorMessage("");

			await updateAdminUser(updatedUser);

			setAdminUsers((prev) =>
				prev.map((item) =>
					item.id === user.id
						? { ...item, isSuperAdmin: updatedUser.isSuperAdmin }
						: item,
				),
			);
		} catch (error) {
			console.error("Update super admin error:", error);
			setErrorMessage(error?.message || "Не удалось обновить роль");
		} finally {
			setActionLoading(false);
		}
	};

	const handleResetPassword = async () => {
		if (!validatePasswordForm()) return;

		try {
			setActionLoading(true);
			setErrorMessage("");

			await resetAdminUserPassword({
				id: passwordForm.id,
				newPassword: passwordForm.newPassword,
				retypeNewPassword: passwordForm.retypeNewPassword,
			});

			setIsPasswordModalOpen(false);
			setPasswordForm(emptyPasswordForm);
		} catch (error) {
			console.error("Reset password error:", error);
			setErrorMessage(error?.message || "Не удалось сбросить пароль");
		} finally {
			setActionLoading(false);
		}
	};

	const handleDelete = (userId) => {
		setUserToDelete(userId);
		setShowConfirmPopup(true);
	};

	const confirmDelete = async () => {
		if (userToDelete === null) return;

		try {
			setActionLoading(true);
			setErrorMessage("");

			await deleteAdminUser(userToDelete);

			setAdminUsers((prev) => prev.filter((user) => user.id !== userToDelete));

			if (expandedUserId === userToDelete) {
				setExpandedUserId(null);
			}
		} catch (error) {
			console.error("Delete error:", error);
			setErrorMessage(error?.message || "Не удалось удалить администратора");
		} finally {
			setActionLoading(false);
			setShowConfirmPopup(false);
			setUserToDelete(null);
		}
	};

	const cancelDelete = () => {
		setShowConfirmPopup(false);
		setUserToDelete(null);
	};

	return (
		<div className="AdminUsers">
			<header className="adminUsers__header">
				<div>
					<p className="adminUsers__eyebrow">Доступы</p>
					<h1>Администраторы</h1>
					<span>Управление аккаунтами модераторов jardulik.</span>
				</div>

				<button type="button" onClick={handleCreateModalOpen}>
					Добавить администратора
				</button>
			</header>

			{errorMessage ? (
				<div className="adminUsers__error">{errorMessage}</div>
			) : null}

			{loading ? (
				<div className="adminUsers__empty">Загрузка...</div>
			) : (
				<div className="adminUsers__list">
					{adminUsers.length === 0 ? (
						<div className="adminUsers__empty">Администраторы не найдены</div>
					) : (
						adminUsers.map((user) => (
							<article key={user.id} className="admin-user-card">
								<button
									type="button"
									className="admin-user-summary"
									onClick={() => handleExpandClick(user.id)}
								>
									<div>
										<h2>{user.fullName || "Без имени"}</h2>
										<p>{user.login || "Логин не указан"}</p>
									</div>

									<div className="adminUsers__badges">
										{user.isSuperAdmin && (
											<span className="adminUsers__role">Super Admin</span>
										)}

										<span
											className={`adminUsers__status ${
												user.isActive ? "active" : "inactive"
											}`}
										>
											{user.isActive ? "Активен" : "Отключён"}
										</span>
									</div>
								</button>

								{expandedUserId === user.id && (
									<div className="admin-user-details">
										<p>Последний вход: {formatDateTime(user.lastLoginDate)}</p>
										<p>Создан: {formatDateTime(user.createDate)}</p>
										<p>Ошибок входа: {user.failedLoginAttempts ?? 0}</p>

										<div className="actions">
											<button
												type="button"
												disabled={actionLoading}
												onClick={() => handleActivateDeactivate(user)}
											>
												{user.isActive ? "Отключить" : "Активировать"}
											</button>

											<button
												type="button"
												disabled={actionLoading}
												onClick={() => handleToggleSuperAdmin(user)}
											>
												{user.isSuperAdmin
													? "Убрать Super Admin"
													: "Сделать Super Admin"}
											</button>

											<button
												type="button"
												disabled={actionLoading}
												onClick={() => handlePasswordModalOpen(user.id)}
											>
												Сбросить пароль
											</button>

											<button
												type="button"
												className="danger"
												disabled={actionLoading}
												onClick={() => handleDelete(user.id)}
											>
												Удалить
											</button>
										</div>
									</div>
								)}
							</article>
						))
					)}
				</div>
			)}

			{isModalOpen && (
				<div className="modal" onMouseDown={handleCreateModalClose}>
					<div
						className="modal-content"
						onMouseDown={(event) => event.stopPropagation()}
					>
						<h3>Новый администратор</h3>

						<input
							type="text"
							placeholder="Имя и фамилия"
							value={newAdminUser.fullName}
							onChange={(event) =>
								setNewAdminUser({
									...newAdminUser,
									fullName: event.target.value,
								})
							}
						/>

						<input
							type="text"
							placeholder="Логин"
							value={newAdminUser.login}
							onChange={(event) =>
								setNewAdminUser({
									...newAdminUser,
									login: event.target.value,
								})
							}
						/>

						<input
							type="password"
							placeholder="Пароль"
							value={newAdminUser.password}
							onChange={(event) =>
								setNewAdminUser({
									...newAdminUser,
									password: event.target.value,
								})
							}
						/>

						<input
							type="password"
							placeholder="Повторите пароль"
							value={newAdminUser.retypePassword}
							onChange={(event) =>
								setNewAdminUser({
									...newAdminUser,
									retypePassword: event.target.value,
								})
							}
						/>

						<label className="adminUsers__check">
							<input
								type="checkbox"
								checked={newAdminUser.isActive}
								onChange={(event) =>
									setNewAdminUser({
										...newAdminUser,
										isActive: event.target.checked,
									})
								}
							/>
							Активен
						</label>

						<label className="adminUsers__check">
							<input
								type="checkbox"
								checked={newAdminUser.isSuperAdmin}
								onChange={(event) =>
									setNewAdminUser({
										...newAdminUser,
										isSuperAdmin: event.target.checked,
									})
								}
							/>
							Супер-админ
						</label>

						<div className="modal-actions">
							<button
								type="button"
								onClick={handleInsert}
								disabled={actionLoading}
							>
								{actionLoading ? "Создание..." : "Создать"}
							</button>

							<button
								type="button"
								onClick={handleCreateModalClose}
								disabled={actionLoading}
							>
								Отмена
							</button>
						</div>
					</div>
				</div>
			)}

			{isPasswordModalOpen && (
				<div className="modal" onMouseDown={handlePasswordModalClose}>
					<div
						className="modal-content"
						onMouseDown={(event) => event.stopPropagation()}
					>
						<h3>Сброс пароля</h3>

						<input
							type="password"
							placeholder="Новый пароль"
							value={passwordForm.newPassword}
							onChange={(event) =>
								setPasswordForm({
									...passwordForm,
									newPassword: event.target.value,
								})
							}
						/>

						<input
							type="password"
							placeholder="Повторите новый пароль"
							value={passwordForm.retypeNewPassword}
							onChange={(event) =>
								setPasswordForm({
									...passwordForm,
									retypeNewPassword: event.target.value,
								})
							}
						/>

						<div className="modal-actions">
							<button
								type="button"
								onClick={handleResetPassword}
								disabled={actionLoading}
							>
								{actionLoading ? "Сохранение..." : "Сохранить"}
							</button>

							<button
								type="button"
								onClick={handlePasswordModalClose}
								disabled={actionLoading}
							>
								Отмена
							</button>
						</div>
					</div>
				</div>
			)}

			<ConfirmPopup
				isOpen={showConfirmPopup}
				onConfirm={confirmDelete}
				onCancel={cancelDelete}
			/>
		</div>
	);
};

export default AdminUsers;
